import { PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Form, Row, Col, message, Breadcrumb } from "antd";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GoBackButton from "../../../components/common/buttons/GoBackButton";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutationOnErrorHandler } from "../../../utils/mutationUtils";
import { useEffect } from "react";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import { getTagByIdRequest, updateTagRequest } from "../../../api/requests/tag";

const updateTagSchemaResolver = yupResolver(
  yup.object().shape({
    name: yup.string(),
    description: yup.string(),
    customAttributes: yup.object().shape({
      type: yup.string(),
      size: yup
        .number()
        .positive("Size must be positive")
        .integer("Size must be an integer"),
      color: yup.string(),
    }),
    active: yup.boolean(),
  }),
);

function UpdateTagPage() {
  const params = useParams();
  const { id } = params;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: tagDetails } = useQuery({
    queryKey: ["tag", id],
    queryFn: async () => {
      const res = await getTagByIdRequest({
        id,
        params: {},
      });
      return res.data?.data;
    },
  });

  const { mutateAsync: updateTag, isPending: updatingTag } = useMutation({
    mutationFn: async (data) => {
      const res = await updateTagRequest({ id, data });
      return res.data;
    },
    mutationKey: ["tag", id],
    onSuccess: (res) => {
      queryClient.invalidateQueries(["tag"]);
      const successMessage = res?.message;
      if (successMessage) {
        message.success(successMessage);
      }
      navigate(-1);
    },
    onError: (error) => {
      mutationOnErrorHandler({ error, message });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: updateTagSchemaResolver,
  });

  async function onSubmit(data) {
    // console.log(data);
    const res = await updateTag(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (tagDetails) {
      const { name, description, customAttributes = {}, active } = tagDetails;
      const { type, size, color } = customAttributes;

      reset({
        name,
        description,
        customAttributes: {
          type,
          size,
          color,
        },
        active,
      });
    }
  }, [tagDetails, reset]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <Link to={"/setup/tags"}>Tags</Link>,
                  },
                  {
                    title: <div className="text-lg">Update</div>,
                  },
                ]}
              />
              <Button
                type="primary"
                htmlType="submit"
                className="flex items-center justify-center"
                loading={updatingTag}
              >
                Update
              </Button>
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                <Col span={12}>
                  <Form.Item
                    label="Name"
                    name="name"
                    validateStatus={errors.name ? "error" : ""}
                    help={errors.name && errors.name.message}
                  >
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input {...field} placeholder="Name" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Description"
                    name="description"
                    validateStatus={errors.description ? "error" : ""}
                    help={errors.description && errors.description.message}
                  >
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <Input {...field} placeholder="Description" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Type"
                    name="customAttributes.type"
                    validateStatus={
                      errors.customAttributes?.type ? "error" : ""
                    }
                    help={errors.customAttributes?.type?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.type"
                      render={({ field }) => (
                        <Input {...field} placeholder="Type" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Size"
                    name="customAttributes.size"
                    validateStatus={
                      errors.customAttributes?.size ? "error" : ""
                    }
                    help={errors.customAttributes?.size?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.size"
                      render={({ field }) => (
                        <Input {...field} placeholder="Size" />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Color"
                    name="customAttributes.color"
                    validateStatus={
                      errors.customAttributes?.color ? "error" : ""
                    }
                    help={errors.customAttributes?.color?.message}
                  >
                    <Controller
                      control={control}
                      name="customAttributes.color"
                      render={({ field }) => (
                        <Input {...field} placeholder="Color" />
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default UpdateTagPage;
